/**
 * Whether the app is currently showing test data and unfinished features.
 *
 * Three ways that is true:
 *  - the sandbox build, for everyone signed in: Majik's screens run on each
 *    person's private sample there, with no switch (docs/DECISIONS.md D-019);
 *  - the person switched Test mode on (real deployments only, and only where
 *    the project turns Test mode on — Majik doesn't, D-019), or
 *  - there is no real data at all — mock mode, used for local development,
 *    previews and the browser tests — where everything is golden records.
 *
 * `canToggle` is false in mock mode, because there is nothing real to switch
 * to — and for anyone who isn't an Admin (`testMode.use`, docs/PERMISSIONS.md).
 * A Staff account that had it on in localStorage before this rule shipped is
 * dropped out here: the server would already refuse the sandbox, but without
 * this they would be stuck showing golden records with no visible way to turn
 * it back off.
 */
import { useEffect } from 'react'
import { can } from '@/core/access'
import { useMe } from '@/features/auth/use-me'
import { isSandbox } from '@/lib/environment'
import { testModeAvailable } from '@/lib/flags'
import { setTestMode, useTestMode } from '@/lib/test-mode'

export function useEffectiveTestMode(options: { enabled?: boolean } = {}): {
  testMode: boolean
  canToggle: boolean
} {
  const toggled = useTestMode()
  const me = useMe(options)
  const real = me.data?.authenticated === true
  const role = me.data?.role
  const canToggle = testModeAvailable && real && role !== undefined && can(role, 'testMode.use')

  useEffect(() => {
    // Also fires when the project has switched the flag off (D-009): anyone
    // stuck showing golden records with it on in localStorage gets dropped
    // out, the same as losing `testMode.use` itself.
    if (real && toggled && !canToggle) setTestMode(false)
  }, [real, toggled, canToggle])

  return { testMode: real ? isSandbox || (canToggle && toggled) : true, canToggle }
}
